import { AsideContext } from '@euler/components/Aside';
import { Center } from '@euler/components/Center';
import { Img } from '@euler/components/image/AdvancedImage';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useServiceFactory } from '@euler/services/factory';
import { safeMarkDevFlag } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { memo, useCallback, useContext, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
  useWindowDimensions,
} from 'react-native';

const kMargin = 0;
const kSizeRatio = 0.618;
const kUseFakeQrcode = safeMarkDevFlag(false);

export const VehicleSubscriptionQrcode = memo(
  ({
    licensePlateNo,
    style,
    onError,
    ...props
  }: {
    licensePlateNo: string;
    style?: StyleProp<ViewStyle>;
    onLoad?: (url: string, size: number) => void;
    onError?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { width: screenWidth } = useWindowDimensions();
    const asideWidth = useContext(AsideContext)?.width;
    const width = asideWidth ?? screenWidth;
    const size = Math.round(width);
    const { orderService } = useServiceFactory();
    const [url, setUrl] = useState<string>();
    const [error, setError] = useState<any>();
    const [loading, setLoading] = useState(true);
    const imageSize = width * kSizeRatio - rpx(kMargin) * 2;

    useEffect(() => {
      orderService
        .getSubscribeBindQrcodeImageUrl({
          lpn: licensePlateNo,
          size,
          __fake__: kUseFakeQrcode,
        })
        .then(setUrl)
        .catch(setError);
    }, [licensePlateNo, orderService, size]);

    const onErrorLoad = useCallback(() => {
      setError('load qrcode image failed');
      setLoading(false);
      onError?.();
    }, [onError]);

    const onLoad = usePersistFn(() => {
      setLoading(false);
      props.onLoad?.(url!, imageSize);
    });

    if (error || !url) {
      return null;
    }

    return (
      <View
        style={style}
        pointerEvents="none"
        css={`
          align-items: center;
        `}
      >
        <Img
          uri={url}
          css={`
            width: ${imageSize}px;
            height: ${imageSize}px;
          `}
          onLoad={onLoad}
          onError={onErrorLoad}
        />
        {loading && (
          <Center style={StyleSheet.absoluteFill}>
            <ActivityIndicator />
          </Center>
        )}
      </View>
    );
  },
);
